import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { TOnRowAddSave, TTableTreeModel } from '../models';
import { ModifiersZTableModel } from '../models/table.modifier';
import { PopoverController } from '@ionic/angular';
import { DropdownComponent } from '../../dropdown/dropdown.component';
import {
  TCellTemplateData,
  TOnTableEditEmit,
  TTableActionTypes,
  TTableAddRowNode,
  TTableEditRowNode,
  TTableInfo,
  TTableRowNode,
  TTableActionListTypes,
  TTableCellTree,
  TActionCellType,
  TTextCell,
  TCssSize,
  TTableCellSize,
} from '../models/table.type';
import { TDropdownItem, TLiftedFormModel, TLiftedInputTypes } from '../../form/model/form.model';
import { FORM_MODIFIER } from '../../form/model/form.modifier';
import { FORM_UTILS } from '../../form/model/form.utils';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Router } from '@angular/router';

@Component({
  selector: 'ui-base-table',
  templateUrl: './base-table.component.html',
  styleUrls: ['./base-table.component.scss'],
})
export class BaseTableComponent<T> {
  uiTableModel!: TTableTreeModel<T>;
  @Input() set zTableModel(value: TTableTreeModel<T>) {
    this.uiTableModel = value;
  }
  @Input() cellSize: TTableCellSize = 'SMALL';
  @Input() cellTextAlign: 'top' | 'middle' | 'bottom' = 'top';
  @Input() showClickOnPlusMsg = false;
  @Input() omitEmptyTableTemplate = false;
  @Input() emptyTableText = 'table_model.NoResults';
  @Input() theme: 'LIGHT' | 'DARK' = 'LIGHT';

  @Output() onRowAction = new EventEmitter<
    [event: Event, rowItem: T, tableActionType: TTableActionTypes, cellInfo: TActionCellType]
  >();
  @Output() onRowEditSave = new EventEmitter<[rowItem: T, payload: TLiftedFormModel['payload']]>();
  @Output() onRowDelete = new EventEmitter<[rowItem: T]>();
  @Output() onActionListItemSelection = new EventEmitter<[rowItem: T, actionListItemType: TTableActionListTypes]>();
  @Output() onRowAddSave = new EventEmitter<TOnRowAddSave>();
  // @Output() onRowAddSave = new EventEmitter<[rowItem: T, payload: TLiftedFormModel['payload']]>();
  @Output() onSelectRow = new EventEmitter<T[]>();
  @Output() onResetRow = new EventEmitter<T>();
  @Output() onTableChange = new EventEmitter<TTableRowNode<T>[]>();
  // @Output() onTableChange = new EventEmitter<TTableInfo<T>['rows']>();
  @Output() onMultiEdit = new EventEmitter<TOnTableEditEmit>();
  @Output() onButtonCLick = new EventEmitter<T>();
  @Output() onToggleChangeEmitter = new EventEmitter<{ rowItem: T; toggleChecked: boolean; rowIndex: number }>();
  @Output() onLabelledToggleChangeEmitter = new EventEmitter<{ val: string; rowIndex: number; cellIndex: number }>();
  @Output() onLinkClicked = new EventEmitter<{ rowItem: T; linkClicked: string; event: Event }>();
  @Output() onExternalEditableInputClicked = new EventEmitter<{ rowIndex: number; cellIndex: number }>();

  @ViewChild(CdkVirtualScrollViewport) viewport!: CdkVirtualScrollViewport;
  constructor(private popoverController: PopoverController, private router: Router) {}
  rowAction(
    $event: Event,
    rowItem: T,
    tableActionType: TTableActionTypes,
    cellInfo: TActionCellType,
    rowIndex?: number,
  ) {
    if (cellInfo.actionNotAllowed) return;
    console.log('[Action]', rowItem, tableActionType, cellInfo, rowIndex);
    $event.stopPropagation();
    this.onRowAction.emit([$event, rowItem, tableActionType, cellInfo]);
  }

  navigateWithState(cellInfo: TTextCell) {
    if (cellInfo.navigationState) {
      this.router.navigate([`/${cellInfo.href}`], { state: cellInfo.navigationState });
    }
  }

  onToggleChange($event: Event, rowItem: T | undefined, rowIndex: number, preventDefault: boolean | undefined) {
    const isToggleChecked = ($event.target as HTMLElement).ariaChecked as string;
    const toggleChecked: boolean = isToggleChecked.toLowerCase() === 'true';
    if (preventDefault) {
      $event.stopImmediatePropagation();
      $event.stopPropagation();
      $event.preventDefault();
      if (rowItem) {
        this.onToggleChangeEmitter.emit({ rowItem, toggleChecked, rowIndex });
      }
    } else {
      if (rowItem) {
        this.onToggleChangeEmitter.emit({ rowItem, toggleChecked: !toggleChecked, rowIndex });
      }
    }
  }

  get translationOffsets(): { top: string; bottom: string } {
    if (!this.viewport || !this.viewport['_renderedContentOffset']) {
      return { top: '-0px', bottom: '-0px' };
    }
    const offset = this.viewport['_renderedContentOffset'];
    return { top: `-${offset}px`, bottom: `${offset}px` };
  }

  removeRow(rowIndex: number) {
    this.uiTableModel = ModifiersZTableModel.removeRow(this.uiTableModel, rowIndex);
    this.#emitSelectRowEvent(this.uiTableModel);
  }

  addRow() {
    this.uiTableModel = ModifiersZTableModel.addRow(this.uiTableModel);
    this.#emitSelectRowEvent(this.uiTableModel);
  }

  editRow(rowIndex: number) {
    this.uiTableModel = ModifiersZTableModel.editRowByIndex(this.uiTableModel, rowIndex);
    this.#emitSelectRowEvent(this.uiTableModel);
  }

  deleteRow(rowIndex: number) {
    const rowItem = this.uiTableModel.tableInfo.rows[rowIndex].rowItem;
    if (rowItem) {
      this.onRowDelete.emit([rowItem]);
    } else {
      console.log('ERROR: Row item not found');
    }
  }

  closeEditRow(rowIndex: number) {
    this.uiTableModel = ModifiersZTableModel.closeEditRow(this.uiTableModel, rowIndex);
    this.onResetRow.emit(this.uiTableModel.tableInfo.rows[rowIndex].rowItem);
  }

  checkIfExternalInputFieldAndSendEvent(cellData: TCellTemplateData<T>) {
    if ('rowIndex' in cellData) {
      if (cellData.cellInfo.type === 'TEXT_CELL' && cellData.cellInfo.isExternalInput) {
        console.log('ext input field clicked.', cellData.cellInfo);
        this.onExternalEditableInputClicked.emit({ rowIndex: cellData.rowIndex, cellIndex: cellData.cellIndex });
      }
    }
  }

  saveEditRow(row: TTableInfo<T>['rows'][number], rowIndex: number) {
    if (row.type === 'ADD_ROW' || row.type === 'EDIT_ROW') {
      if (row.formModel?.isValid) {
        switch (row.type) {
          case 'ADD_ROW': {
            if (row.formModel.isValid) {
              this.onRowAddSave.emit({ payload: row.formModel.payload });
            } else {
              console.log('[ERROR] Row Item not defined', row);
            }
            break;
          }
          case 'EDIT_ROW': {
            this.onRowEditSave.emit([row.rowItem, row.formModel.payload]);
            break;
          }
        }
      } else {
        const updatedformModel = FORM_MODIFIER.markTouchedInputs(row.formModel);
        this.uiTableModel = ModifiersZTableModel.updateFormModelByIndex(this.uiTableModel, rowIndex, updatedformModel);
      }
    }
  }

  #emitSelectRowEvent(tableTreeModel: TTableTreeModel<T>) {
    const { rows } = tableTreeModel.tableInfo;
    const selectedRows = rows.filter(
      (v) => v.type === 'ROW' && !!v.cells.find((c) => c.type === 'SELECT_CELL' && c.isChecked),
    ) as TTableRowNode<T>[];
    this.onSelectRow.emit(selectedRows.map((v) => v.rowItem));
  }
  toggleHeaderSelection($event: Event) {
    const isCheckbox = ($event.target as HTMLDivElement).classList.contains('ui-table__select-cell');
    const notCheckBox = !isCheckbox;
    if (notCheckBox) {
      $event.preventDefault();
      return;
    }
    if (!this.uiTableModel.isEditEnabled) {
      this.uiTableModel = ModifiersZTableModel.selectAllRows(this.uiTableModel);
      this.#emitSelectRowEvent(this.uiTableModel);
    }
  }
  toggleRowSelection($event: Event, index: number) {
    const isCheckbox = ($event.target as HTMLDivElement).classList.contains('ui-table__select-cell');
    const notCheckBox = !isCheckbox;
    if (notCheckBox) {
      $event.preventDefault();
      return;
    }
    if (!this.uiTableModel.isEditEnabled) {
      this.uiTableModel = ModifiersZTableModel.selectRowByIndex(this.uiTableModel, index);
      this.#emitSelectRowEvent(this.uiTableModel);
    }
  }

  updateUserInputCell() {
    ModifiersZTableModel.updateTableModel(this.uiTableModel);
    const { rows } = this.uiTableModel.tableInfo;
    this.onTableChange.emit(rows.filter((v) => v.type === 'ROW') as TTableRowNode<T>[]);
  }

  trackByFn(index: number) {
    return index;
  }

  trackCellByFn(index: number, cell: TTableCellTree) {
    return `${index}_${cell.stickyPosition}`;
  }

  typedCellNode(value: unknown) {
    return value as TCellTemplateData<T>;
  }

  onChange(
    $event: Event,
    existingInput: TLiftedFormModel<T>['inputs'][number],
    index: number,
    rowIndex: number,
    isOtherOptionValue = false,
  ) {
    const updatedInput = FORM_UTILS.updateInputByEvent($event, existingInput, isOtherOptionValue);
    this.#onInputUpdate(updatedInput, index, rowIndex);
  }

  async showActionListPopover($event: Event, cellInfo: TTableCellTree, rowItem?: T) {
    if (cellInfo.type === 'ACTION_LIST_CELL') {
      const popover = await this.popoverController.create({
        component: DropdownComponent,
        componentProps: {
          items: cellInfo.actions,
          title: cellInfo.title,
        },
        event: $event,
        showBackdrop: false,
        cssClass: 'dropdown-popover',
        dismissOnSelect: true,
      });
      popover.present();

      popover.onDidDismiss().then(({ data }) => {
        if (rowItem) {
          this.onActionListItemSelection.emit([rowItem, data]);
        } else {
          console.log('ERROR: Row Item is not defined');
        }
      });
    }
  }

  sortTableByComunIndex(index: number) {
    this.uiTableModel = ModifiersZTableModel.sortTableByColumnIndex(this.uiTableModel, index);
  }

  //LABELLED TOGGLE CELL
  toggleCell(val: string, rowIndex: number, cellIndex: number) {
    this.onLabelledToggleChangeEmitter.emit({ val, rowIndex, cellIndex });
  }

  //IMAGE CELL ACTION
  showImage(rowIndex: number, cellIndex: number) {
    this.uiTableModel = ModifiersZTableModel.updateImageVisibility(this.uiTableModel, { rowIndex, cellIndex }, false);
  }

  hideImage(rowIndex: number, cellIndex: number) {
    this.uiTableModel = ModifiersZTableModel.updateImageVisibility(this.uiTableModel, { rowIndex, cellIndex }, true);
  }
  onDropdownListSelection(
    $event: TDropdownItem['value'][],
    existingInput: TLiftedFormModel<T>['inputs'][number],
    index: number,
    rowIndex: number,
  ) {
    const updatedInput = FORM_UTILS.updateInputBySelectedList($event, existingInput);
    this.#onInputUpdate(updatedInput, index, rowIndex);
  }

  #onInputUpdate(updatedInput: TLiftedInputTypes<T>, index: number, rowIndex: number) {
    const rowsToUpdate = this.uiTableModel.tableInfo.rows[rowIndex];
    switch (rowsToUpdate.type) {
      case 'ADD_ROW':
      case 'EDIT_ROW': {
        const updatedformModel = FORM_MODIFIER.updateInputByIndex(rowsToUpdate.formModel, updatedInput, index);
        this.uiTableModel = ModifiersZTableModel.updateFormModelByIndex(this.uiTableModel, rowIndex, updatedformModel);

        const formsRow = this.uiTableModel.tableInfo.rows.filter(
          (v) => v.type === 'ADD_ROW' || v.type === 'EDIT_ROW',
        ) as (TTableAddRowNode<T> | TTableEditRowNode<T>)[];

        const output = formsRow.map((v) => ({
          isValid: v.formModel.isValid || false,
          payload: v.formModel.payload || {},
        }));
        const isAllFormValid = output.reduce((acc, v) => acc && v.isValid, true);
        this.onMultiEdit.emit({
          isValid: isAllFormValid,
          edits: output,
        });
      }
    }
  }

  getMarkerCustomSize(marker: TTextCell['marker'], property: 'height' | 'width'): string {
    if (property === 'height')
      return marker?.customSize?.height ? marker.customSize.height.value + marker.customSize.height.unit : '15px';
    else return marker?.customSize?.width ? marker.customSize.width.value + marker.customSize.width.unit : '15px';
  }

  getTableCellSize(cellSize: TTableCellSize): string | null {
    return typeof cellSize === 'object' ? cellSize.size.value + cellSize.size.unit : cellSize;
  }

  getSizeStringFromCssSize(size?: TCssSize): string | undefined {
    return size && size.value + size.unit;
  }
}
